import React, { useLayoutEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import './i18n/index';
import './index.css';
import App from './App';
import { clearApiAuth, configureApiAuth } from './lib/api';
import { expireSession, invalidateSession } from './lib/session';
import { applyTheme, loadTheme } from './themes';

// Apply saved or default theme before paint
applyTheme(loadTheme());

// Clean up legacy localStorage data on app startup
(() => {
  try {
    localStorage.removeItem('compliancecheck-store');
  } catch (error) {
    console.debug('Could not clean up legacy localStorage:', error);
  }
})();

// Auth0 configuration
const domain = import.meta.env.VITE_AUTH0_DOMAIN || '';
const clientId = import.meta.env.VITE_AUTH0_CLIENT_ID || '';
const audience =
  import.meta.env.VITE_AUTH0_AUDIENCE || 'https://compliancecheck-api';

export function ApiAuthBootstrap() {
  const { getAccessTokenSilently, isAuthenticated, isLoading, user } =
    useAuth0();
  useLayoutEffect(() => {
    invalidateSession();
    configureApiAuth({
      getAccessToken: async () => {
        if (isLoading) throw new Error('Authentication is still loading');
        return isAuthenticated ? getAccessTokenSilently() : null;
      },
      onSessionExpired: expireSession,
    });
    return () => {
      clearApiAuth();
      invalidateSession();
    };
  }, [getAccessTokenSilently, isAuthenticated, isLoading, user?.sub]);
  return null;
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      authorizationParams={{
        redirect_uri: window.location.origin + '/auth/callback',
        audience: audience,
      }}
      cacheLocation="localstorage"
      useRefreshTokens={true}
    >
      <ApiAuthBootstrap />
      <App />
    </Auth0Provider>
  </React.StrictMode>
);
